import type { RecentEntry } from './types';

/**
 * Insert `entry` into `existing`, replacing any prior entry that represents
 * the same file, then cap the result to `max` (newest first).
 *
 * Identity: two entries are the "same file" when BOTH have a truthy
 * `locator` and those locators are equal — this is what makes re-opening a
 * file (even after an edit/save changes its content-hash `id`) refresh a
 * single row instead of appending a new one. When either entry lacks a
 * locator (e.g. an unsaved document, or the web build without File System
 * Access support), identity falls back to matching `id`, which is the
 * previous behavior and keeps distinct locator-less opens as distinct rows.
 *
 * Pure: no storage access, no clock — callers own persistence and `openedAt`.
 */
export function upsertRecent(existing: readonly RecentEntry[], entry: RecentEntry, max: number): RecentEntry[] {
  const isSameFile = (other: RecentEntry): boolean =>
    other.locator && entry.locator ? other.locator === entry.locator : other.id === entry.id;
  const filtered = existing.filter((other) => !isSameFile(other));
  return [entry, ...filtered].slice(0, max);
}
